import { UNITS, TIME_UNITS } from './misc.types'

export type BreakPoint = {
  highBreakPoint: {
    quantity: number
    unit: `${UNITS.MONTH}` | `${UNITS.YEAR}` | `${UNITS.RUPEES}`
  }
  lowBreakPoint: {
    quantity: number
    unit: `${UNITS.MONTH}` | `${UNITS.YEAR}` | `${UNITS.RUPEES}`
  }
  rate: {
    amount: number
    unit: `${UNITS.PERCENTAGE}`
  }
}

export interface Tenure {
  value: number
  period: `${TIME_UNITS}`
}

type Manager = {
  name: string
  date: string
  twitter: string | null
  linkedIn: string | null
  newFundManager: boolean
}

export type AUMChange = {
  tenure: Tenure
  value: number
}

export type DistributionDatum = { category: string; weightage: number }

export type AssetAllocation = {
  nature: 'EQUITY' | 'DEBT' | 'OTHERS'
  allocationPercentage: number
  types: string[]
  distributionTypeToHoldings: {
    [x: string]: DistributionDatum[]
  }
}

type TrailingReturns = {
  tenure: Tenure | null
  returns: {
    unit: `${UNITS.PERCENTAGE}`
    value: number
  }
  oneDayReturn: number
  principal: number
  returnsSinceInception: number
}

type RollingReturns = {
  tenure: Tenure
  returns: {
    unit: `${UNITS.PERCENTAGE}`
    value: number
  }
  comparisonPercentage: number
}

type AdvancedRatios = {
  alpha: number | null
  beta: number | null
  sortino: number | null
  sharpe: number | null
  pe: number | null
  pb: number | null
}

type FundDetails = {
  fundName: string
  fundHouse: string
  fundNature: 'EQUITY' | 'DEBT' | 'MIXED' | 'INDEX'
  purchaseMode: 'DIRECT' | 'REGULAR'
  category: 'SMALL_CAP' | 'LARGE_CAP' | 'MID_CAP' | 'FLEXI_CAP' | 'CREDIT_RISK'
  navToday: number | null
  lastSyncDate: number | null
  exitLoad: null | BreakPoint[]
  expenseRatio: {
    amount: number
    unit: `${UNITS.PERCENTAGE}`
  }
  lockInPeriod: Tenure | null
  launchedOn: number
  minSip: {
    amount: number
    unit: `${UNITS.RUPEES}`
  } | null
  benchMarkIndex: string
}

export enum MutualFundDetailSections {
  HEADER_ACCORDIONS = 'HEADER_ACCORDIONS',
  UNAVAILABLE_SECTIONS = 'UNAVAILABLE_SECTIONS',
  RETURNS_GRAPH = 'RETURNS_GRAPH',
  ELITE_STATS = 'ELITE_STATS',
  FUND_HOLDINGS = 'FUND_HOLDINGS',
  FUND_INFORMATION = 'FUND_INFORMATION',
  FUND_OBJECTIVE = 'FUND_OBJECTIVE'
}

export enum MutualFundDetailSubSections {
  MY_INVESTMENTS = 'MY_INVESTMENTS',
  POWER_PERFORMANCE = 'POWER_PERFORMANCE',
  ALERTS = 'ALERTS',
  TRAILING_RETURNS = 'TRAILING_RETURNS',
  ROLLING_RETURNS = 'ROLLING_RETURNS',
  SUGGESTED_HOLDING_PERIOD = 'SUGGESTED_HOLDING_PERIOD',
  MARKET_OUT_PERFORMANCE = 'MARKET_OUT_PERFORMANCE',
  CONSISTENCY = 'CONSISTENCY',
  VOLATILITY = 'VOLATILITY',
  RECENCY = 'RECENCY',
  HOLDINGS = 'HOLDINGS',
  ASSET_ALLOCATION = 'ASSET_ALLOCATION',
  ADVANCED_RATIOS = 'ADVANCED_RATIOS',
  TAXES = 'TAXES',
  TAX_LIABILITY = 'TAX_LIABILITY',
  FUND_MANAGERS = 'FUND_MANAGERS',
  ABOUT_FUND = 'ABOUT_FUND'
}

export type MutualFundDetailSectionsType = {
  section: `${MutualFundDetailSections}`
  subSections: `${MutualFundDetailSubSections}`[]
}[]

type BenchmarkFund = {
  value: string
  type: string
  name: string
}

export enum WaterfallValues {
  YES = 'YES',
  YES_DIRECT = 'YES_DIRECT',
  NO = 'NO'
}

export enum WaterfallSections {
  ROLLING_RETURNS = 'rollingReturns',
  ELITE = 'elite',
  HERO = 'hero',
  RANK = 'rankTab',
  PEER = 'peerTab'
}

export enum TaxCategories {
  EQUITY = 'EQUITY',
  DEBT = 'DEBT',
  ELSS = 'ELSS',
  NON_EQUITY = 'NON_EQUITY',
  HYBRID_EQUITY = 'HYBRID_EQUITY',
  HYBRID_DEBT = 'HYBRID_DEBT',
  HYBRID_NON_EQUITY = 'HYBRID_NON_EQUITY',
  NON_EQUITY_OTHERS = 'NON_EQUITY_OTHERS',
  OTHERS = 'OTHERS',
  GIFT_PPFAS = 'GIFT_PPFAS'
}

export type PurchaseMode = 'DIRECT' | 'REGULAR'

export type FundNature = 'EQUITY' | 'DEBT' | 'MIXED' | 'INDEX'

export type FundCategory = 'SMALL_CAP' | 'LARGE_CAP' | 'MID_CAP' | 'FLEXI_CAP' | 'CREDIT_RISK'

export type MetaDataResponse = {
  responseType: string
  responseName: string
  fundDetails: FundDetails
  defaultBenchmarkFund: BenchmarkFund
  taxCategory: `${TaxCategories}`
  fundManagers: Manager[]
  objective: string
  aum: string
  aumChange: AUMChange[]
  distribution: AssetAllocation[]
  fundLogo: string
  trailingReturns: TrailingReturns
  rollingReturns: RollingReturns
  sections: MutualFundDetailSectionsType
  advancedRatios: AdvancedRatios
  stampDuty: {
    amount: number
    unit: `${UNITS.PERCENTAGE}`
  }
  portfolioInsightsLockedInfo: {
    status: 'LOCKED' | 'GRACE_PERIOD' | 'UNLOCKED'
    gracePeriodEndTimestamp: number
  }
  fundHoldingsCount: number
  benchmarkFunds: BenchmarkFund[]
  investmentStatus: 'CLOSE' | 'OPEN'
  distributionStatus: 'IDCW' | 'Growth'
  fundFamilyDirectFundIsin: string
  notRatedCategory: boolean
  sectionAvailability: { [key in `${WaterfallSections}`]: `${WaterfallValues}` } & {
    unavailabilityReason: string
  }
  fundHouseMetaData: {
    name: string | null
    totalAum: number | null
    rankAsPerAum: number
    foundedOn: number
    aboutUs: string | null
  }
  eliteIconCta: Record<any, any>
}

export enum ChangeDirection {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  NEUTRAL = 'NEUTRAL'
}

export type NameValueDatum = {
  name: string
  value: string
  nameClassName?: string
  valueClassName?: string
  nameContent?: React.ReactNode
  valueContent?: React.ReactNode
  /** Overrides default grouped aria-label when groupForAccessibility is enabled. */
  accessibilityLabel?: string
}

/* Rolling Returns  */
export type FundPerformanceData = {
  x_value: number
  y_value: number
  label: number | null
}

export type RollingReturnsPerformanceData = {
  responseType: string
  responseName: string
  currFunds: FundPerformanceData[]
  benchmarkFunds: FundPerformanceData[]
  labelUnit: 'PERCENTAGE'
  yunit: 'PERCENTAGE'
}

export type TrailingPerformaceDatum = {
  label: number
  x_value: number
  y_value: number
}

export type TrailingReturnsPerformanceData = {
  responseType: 'MUTUAL_FUND_L2'
  responseName: 'MUTUAL_FUND_L2'
  currentFundReturns: {
    fundAmount: {
      amount: number
      unit: 'RUPEES'
    }
    returns: {
      unit: 'PERCENTAGE'
      value: number
    }
  }
  benchmarkReturns: {
    fundAmount: {
      amount: number
      unit: 'RUPEES'
    }
    returns: {
      unit: 'PERCENTAGE'
      value: number
    }
  }
  currFunds: TrailingPerformaceDatum[]
  benchmarkFunds: TrailingPerformaceDatum[]
  labelUnit: `${UNITS.PERCENTAGE}`
  toggleValues: { value: number; period: `${UNITS.MONTH}` | `${UNITS.YEAR}` | `${UNITS.ALL}` | `${UNITS.DAY}` }[]
  yunit: `${UNITS.RUPEES}`
}

type PeerFund = {
  tenure: {
    value: number
    period: `${UNITS.MONTH}` | `${UNITS.YEAR}`
  }
  peersData: {
    isin: string
    fundName: string
    rank: number
    fundReturns: {
      unit: `${UNITS.PERCENTAGE}`
      value: number
    }
  }[]
}

export type PeerResponse = {
  responseType: 'MUTUAL_FUND_L2'
  responseName: 'MUTUAL_FUND_L2'
  categoryCount: number
  peers: PeerFund[]
}

export enum EliteScoreTrends {
  GREAT = 'GREAT',
  GOOD = 'GOOD',
  AVERAGE = 'AVERAGE',
  BAD = 'BAD'
}

export interface ReturnsRankPoint {
  x_value: number
  y_value: number
  label: number
  tag: `${EliteScoreTrends}`
}

export type RanksDatum = {
  tenure: Tenure
  returnsRankList: ReturnsRankPoint[]
  currentRank: number
}

export type RanksData = {
  responseType: 'ROLLING_RETURNS_RANK' | 'TRAILING_RETURNS_RANK'
  responseName: 'ROLLING_RETURNS_RANK' | 'TRAILING_RETURNS_RANK'
  categoryCount: number
  rollingReturnsRanks?: RanksDatum[]
  trailingReturnsRanks?: RanksDatum[]
  updationTime: {
    recurringFrequencyType: 'DAILY' | 'WEEKLY' | 'MONTHLY'
    value: number
  }
  labelUnit: 'PERCENTAGE' | 'CURRENCY' | string
}

export interface RollingsRanksData {
  responseType: 'ROLLING_RETURNS_RANK'
  responseName: 'ROLLING_RETURNS_RANK'
  categoryCount: number
  rollingReturnsRanks: {
    tenure: Tenure
    returnsRankList: ReturnsRankPoint[]
    currentRank: number
  }[]
  updationTime: {
    recurringFrequencyType: 'DAILY' | 'WEEKLY' | 'MONTHLY'
    value: number
  }
  labelUnit: 'PERCENTAGE' | 'CURRENCY' | string
}

export interface TrailingRanksData {
  responseType: 'TRAILING_RETURNS_RANK'
  responseName: 'TRAILING_RETURNS_RANK'
  categoryCount: number
  trailingReturnsRanks: {
    tenure: Tenure
    returnsRankList: ReturnsRankPoint[]
    currentRank: number
  }[]
  updationTime: {
    recurringFrequencyType: 'DAILY' | 'WEEKLY' | 'MONTHLY'
    value: number
  }
  labelUnit: 'PERCENTAGE' | 'CURRENCY' | string
}

export type TaxInfo = {
  gains: number
  investedAmount: number
  taxRate: number
  taxableAmount: number
}

export type TaxLiabilityDataResponseType = {
  currentValue: number
  exitLoad: number
  stcgTaxInfo: TaxInfo
  ltcgTaxInfo: TaxInfo
  ltcgExemptionApplicable: boolean
  grandfatheringRuleApplicable: boolean
  fundType: string
  taxCategory: `${TaxCategories}`
}

export interface HoldingsDistribution {
  name: string
  weightage: number
}

export interface HoldingsDataType {
  responseType: string
  responseName: string
  investmentDistribution: HoldingsDistribution[]
  sectorDistribution: HoldingsDistribution[]
}

type UnitAmount = {
  unit: 'RUPEES'
  amount: number
}

type PercentageReturn = {
  unit: 'PERCENTAGE'
  value: number
}

export type FolioDetail = {
  folioNumber: string
  folioName: string
  folioLogo: string
  source: `${TransactionSource}`
  folioWealth: UnitAmount
}

export type MemberInvestmentEntryType = {
  userName?: string
  temporaryFamilyAccessToken?: string
  currentGain: UnitAmount
  investedAmount: UnitAmount
  investmentDetails?: {
    puInvestmentAmount: UnitAmount
    externalInvestmentAmount: UnitAmount
  }
  sipAmount?: UnitAmount
  fundReturns?: {
    fundAmount: UnitAmount
    returns: PercentageReturn
  }
  oneDayReturns?: {
    fundAmount: UnitAmount
    returns: PercentageReturn
  }
  xirr?: PercentageReturn | null
  averageNav?: UnitAmount
  balancedUnits?: number
  firstInvestmentTimeStamp?: number
  folioDetails?: FolioDetail[]
  throughFamilyAccess?: boolean
  secondaryUserName?: string
  primaryUserName?: string
}

export type InvestmentDataType = {
  currentGain: UnitAmount
  investedAmount: UnitAmount
  investmentDetails: {
    puInvestmentAmount: UnitAmount
    externalInvestmentAmount: UnitAmount
  }
  sipAmount: UnitAmount
  fundReturns: {
    fundAmount: UnitAmount
    returns: PercentageReturn
  }
  oneDayReturns: {
    fundAmount: UnitAmount
    returns: PercentageReturn
  }
  xirr: PercentageReturn | null
  averageNav: UnitAmount
  balancedUnits: number
  firstInvestmentTimeStamp: number
  folioDetails: FolioDetail[]
  throughFamilyAccess: boolean
  secondaryUserName: string
  primaryUserName: string
  memberInvestmentData?: MemberInvestmentEntryType[]
}

export enum TransactionTypes {
  BUY = 'BUY',
  SELL = 'SELL',
  SIP = 'SIP'
}

export enum TransactionSource {
  POWERUP = 'POWERUP',
  EXTERNAL = 'EXTERNAL'
}

export type InvestmentTransaction = {
  units: number
  nav: {
    unit: 'RUPEES'
    amount: number
  }
  txnAmount: {
    unit: 'RUPEES'
    amount: number
  }
  timestamp: number
  type: `${TransactionTypes}`
  source: `${TransactionSource}`
}

export type InvestmentTransactionList = {
  userTxnResponseList: InvestmentTransaction[]
  responseName: 'MUTUAL_FUND_L2'
  responseType: 'MUTUAL_FUND_L2'
  itemsCount: number
}

export enum PowerPerformanceForms {
  IN_FORM = 'IN_FORM',
  ON_TRACK = 'ON_TRACK',
  OFF_TRACK = 'OFF_TRACK',
  OUT_OF_FORM = 'OUT_OF_FORM',
  NOT_RATED = 'NOT_RATED',
  PERFORMANCE_LOCKED = 'PERFORMANCE_LOCKED'
}

export type PowerPerformanceRankPoint = {
  x_value: number
  y_value: `${PowerPerformanceForms}`
  label: number
}

export enum UserInvestmentType {
  NONE = 'NONE',
  SIP = 'SIP',
  LUMPSUM = 'LUMPSUMP'
}

export enum ELiteMembershipStatus {
  REGULAR = 'REGULAR',
  ELITE = 'ELITE'
}

export interface PowerPerformanceRankDataType {
  responseType: 'MUTUAL_FUND_L2'
  responseName: 'MUTUAL_FUND_L2'
  userInvestmentType: `${UserInvestmentType}`
  eliteMemberShipStatus: `${ELiteMembershipStatus}`
  performanceRankList: PowerPerformanceRankPoint[]
  isFirstTimeElite: boolean
  isPowerRankAvailable: boolean
}

export enum PowerPerformanceRiveStates {
  ELITE = 'elite',
  NON_ELITE = 'non-elite',
  UNRATED = 'unrated',
  UNABLE_TO_LOAD = 'unable-to-load'
}

export type PowerPerformanceRiveStateType = `${PowerPerformanceRiveStates}` | null

export enum AlertsTypes {
  OUT_OF_FORM_ALERT = 'OUT_OF_FORM',
  REGULAR_FUND_ALERT = 'REGULAR'
}

export type AlertsResponseType = {
  responseType: 'MF_ALERT_CARD'
  responseName: 'MF_ALERT_CARD'
  listOfAlerts: `${AlertsTypes}`[]
}

export type ScoreHistoryList = {
  x_value: number
  y_value: number
  label: number
  tag: `${EliteScoreTrends}`
}[]

export type PowerRank = {
  powerRank: number
  outOf: number
}

export type EliteScoresResponseType = {
  isin: string
  powerRank: PowerRank['powerRank']
  categoryCount: PowerRank['outOf']
  category: string
  returnsScoreList: ScoreHistoryList
  stabilityScoreList: ScoreHistoryList
  recencyScoreList: ScoreHistoryList
}

export type EliteScoreLeaderboardResponseType = {
  powerScorePeerList: { isin: string; fundName: string; score: number; rank: number }[]
}

export enum EliteScoresTypes {
  CONSISTENCY = 'CONSISTENCY',
  RECENCY = 'RECENCY',
  VOLATILITY = 'VOLATILITY'
}

export type ElitePowerRankLeaderboardResponseType = {
  rankList: { isin: string; fundName: string; powerRank: number }[]
}

export type RegularVsDirectDataResponseType = {
  fundValues: RegularVsDirectFundValue[]
}

export type RegularVsDirectFundValue = {
  regular: RegularVsDirectFundData
  direct: RegularVsDirectFundData
  diff: { value: number }
}

export type RegularVsDirectFundData = {
  isin: string
  value: number
}

export type KnowWhyAPIReponseType = {
  isElite: boolean
  isRegular: boolean
  currentGain: UnitAmount
  investedAmount: UnitAmount
  sipAmount: UnitAmount
  isin: string
  logo: string
  name: string
  nature: string
  category: string
  categoryAvgComparisonDetails: {
    benchmarkComparisonResult: 'LOWER' | 'HIGHER' | 'NEUTRAL'
    defaultReturns: FinancialMetric
    benchmarkComparisonBeatingTenures: number
    benchmarkComparisonTotalTenures: number
    comparisonCopyType: 'BEATING_TENURE_COMPARISON' | 'GENERIC'
  }
  trailingReturns: FinancialMetric[]
  rollingReturns: FinancialMetric[]
  powerPerformanceData: PowerPerformanceData
  scoringDetails: ScoringDetails
  recommendedFunds: RecommendedFund[]
}

export enum FinancialMetricTypes {
  TRAILING_RETURNS = 'TRAILING_RETURNS',
  ROLLING_RETURNS = 'ROLLING_RETURNS',
  AUM = 'AUM'
}

export enum ToggleLabel {
  TRAILING_RETURNS = 'Trailing Returns',
  ROLLING_RETURNS = 'Rolling Returns',
  AUM = 'AUM'
}

export type FinancialMetric = {
  metricType: `${FinancialMetricTypes}`
  tenure?: Tenure // No Tenure for AUM
  money: {
    amount: number
    unit: `${UNITS.PERCENTAGE}`
  }
}

export type PowerPerformanceData = {
  timeStamp: number
  powerRating: `${PowerPerformanceForms}`
  powerRank: number
  categoryCount: number
}

export type ScoringDetails = {
  recencyScore: number
  consistencyScore: number
  volatilityScore: number
  maxScore: number
  consistencyScorePerformanceTag: `${EliteScoreTrends}`
  recencyScorePerformanceTag: `${EliteScoreTrends}`
  volatilityScorePerformanceTag: `${EliteScoreTrends}`
}

export type RecommendedFund = {
  isin: string
  logo: string
  name: string
  nature: string
  category: string
  financialMetrics: FinancialMetric[]
  powerPerformanceData: PowerPerformanceData
  scoringDetails: ScoringDetails
}

export type PerformanceLabel = 'GREAT' | 'GOOD' | 'AVERAGE' | 'BAD' | 'UNKNOWN'
